/**
 * TaskToast — Facebook-style slide-in toast for new task notifications.
 *
 * Sits in the bottom-right corner, stackable up to 3, auto-dismisses after
 * 8s. Click anywhere on the toast (except the X) to jump to /tasks. Hovering
 * pauses the auto-dismiss timer.
 *
 * Listens to NotificationContext — whenever `unread` (task notifications)
 * grows, the newest unread notifications are surfaced as toasts.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckSquare } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';

const DISMISS_MS = 8_000;
const MAX_STACK = 3;

const TaskToast = () => {
  const navigate = useNavigate();
  const { notifications, loaded } = useNotifications();
  const [visible, setVisible] = useState([]); // [{id, message, type, created_at, hovered}]
  const seenIdsRef = useRef(null); // null = uninitialised (first snapshot becomes baseline)

  // Detect NEW unread notifications and enqueue toasts for them.
  // The very first poll is treated as a baseline so we don't toast existing
  // unread tasks the user has already seen previously.
  useEffect(() => {
    // Wait for the first real fetch, otherwise the empty initial state becomes
    // the baseline and every already-seen unread item gets re-toasted.
    if (!loaded) return;
    if (seenIdsRef.current === null) {
      seenIdsRef.current = new Set(notifications.map((n) => n.id));
      return;
    }
    const fresh = notifications.filter(
      (n) => !n.read && !seenIdsRef.current.has(n.id),
    );
    if (fresh.length === 0) return;
    fresh.forEach((n) => seenIdsRef.current.add(n.id));
    setVisible((curr) => [...fresh.reverse(), ...curr].slice(0, MAX_STACK));
  }, [notifications, loaded]);

  const dismiss = (id) => setVisible((curr) => curr.filter((t) => t.id !== id));

  const handleClick = (id) => {
    dismiss(id);
    navigate('/tasks');
  };

  if (visible.length === 0) return null;

  return (
    <div
      data-testid="task-toast-stack"
      className="fixed bottom-6 right-24 z-[110] flex flex-col gap-3 pointer-events-none"
      style={{ maxWidth: 'calc(100vw - 3rem)' }}
    >
      {visible.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} onOpen={() => handleClick(t.id)} />
      ))}
    </div>
  );
};

const ToastCard = ({ toast, onDismiss, onOpen }) => {
  const [hovered, setHovered] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef(null);

  // Auto-dismiss with hover-pause. onDismiss is intentionally kept in a ref:
  // the parent re-renders on every notification poll, and depending on the
  // callback identity restarted this timer before it could ever fire.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (hovered || leaving) return undefined;
    timerRef.current = setTimeout(() => {
      setLeaving(true);
      setTimeout(() => onDismissRef.current(), 250); // wait for slide-out animation
    }, DISMISS_MS);
    return () => clearTimeout(timerRef.current);
  }, [hovered, leaving]);

  return (
    <div
      data-testid={`task-toast-${toast.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onOpen}
      role="alert"
      className={`pointer-events-auto cursor-pointer w-[360px] max-w-full rounded-2xl bg-white shadow-2xl border border-mute/40 overflow-hidden transform transition-all duration-300 ${leaving ? 'translate-x-6 opacity-0' : 'translate-x-0 opacity-100'}`}
      style={{ animation: leaving ? 'none' : 'task-toast-in 280ms cubic-bezier(0.16, 1, 0.3, 1)' }}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="shrink-0 h-9 w-9 rounded-full bg-primary/10 text-primary grid place-items-center">
          <CheckSquare className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-primary  tracking-wider mb-0.5">New task</div>
          <div className="text-sm text-ink leading-snug line-clamp-3">{toast.message}</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setLeaving(true); setTimeout(onDismiss, 250); }}
          className="shrink-0 h-7 w-7 grid place-items-center rounded-full text-ink/40 hover:text-ink hover:bg-surface-100 transition-colors"
          aria-label="Dismiss"
          data-testid={`task-toast-${toast.id}-dismiss`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Progress bar — visually communicates the auto-dismiss timer */}
      <div className="h-0.5 bg-surface-100 overflow-hidden">
        <div
          className="h-full bg-primary origin-left"
          style={{
            animation: hovered || leaving ? 'none' : `task-toast-progress ${DISMISS_MS}ms linear forwards`,
          }}
        />
      </div>
    </div>
  );
};

export default TaskToast;
